// src/pages/RafflePage.jsx - COMPLETE WORKING VERSION
import React, { useState, useEffect, useCallback } from 'react';
import { raffleService } from '../services/raffleService';
import './RafflePage.css';

const RafflePage = ({ user, addNotification }) => {
    const [activeTab, setActiveTab] = useState('events');
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [myTickets, setMyTickets] = useState([]);
    const [myWinnings, setMyWinnings] = useState([]);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [ticketQuantity, setTicketQuantity] = useState(1);
    const [pendingPayment, setPendingPayment] = useState(null);
    const [bankDetails, setBankDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [countdowns, setCountdowns] = useState({});
    
    // Payment modal states
    const [referenceNumber, setReferenceNumber] = useState('');
    const [paymentNotes, setPaymentNotes] = useState('');
    const [proofImage, setProofImage] = useState('');
    const [fileName, setFileName] = useState('');
    const [uploading, setUploading] = useState(false);

    // Load data
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [eventsRes, ticketsRes, winningsRes, settingsRes] = await Promise.all([
                raffleService.getActiveEvents(),
                raffleService.getMyTickets(),
                raffleService.getMyWinnings(),
                raffleService.getSettings()
            ]);
            
            if (eventsRes.success) setEvents(eventsRes.events || []);
            if (ticketsRes.success) setMyTickets(ticketsRes.tickets || []);
            if (winningsRes.success) setMyWinnings(winningsRes.winnings || []);
            if (settingsRes.success) setBankDetails(settingsRes.settings);
            
        } catch (error) {
            console.error('Load data error:', error);
            addNotification('Failed to load raffle data', 'error');
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Reset payment form when modal closes
    useEffect(() => {
        if (!showPaymentModal) {
            setReferenceNumber('');
            setPaymentNotes('');
            setProofImage('');
            setFileName('');
            setUploading(false);
        }
    }, [showPaymentModal]);

    // Update countdown timers
    useEffect(() => {
        const interval = setInterval(() => {
            const newCountdowns = {};
            const now = Date.now();
            
            events.forEach(event => {
                if (event.draw_date) {
                    const drawTime = new Date(event.draw_date).getTime();
                    const diff = drawTime - now;
                    
                    if (diff > 0) {
                        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                        newCountdowns[event.id] = { days, hours, minutes, seconds, total: diff };
                    } else {
                        newCountdowns[event.id] = { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0, expired: true };
                    }
                }
            });
            
            setCountdowns(newCountdowns);
        }, 1000);
        
        return () => clearInterval(interval);
    }, [events]);

    // Handle purchase
    const handlePurchase = async () => {
        if (!selectedEvent) {
            addNotification('Please select an event first', 'error');
            return;
        }
        
        if (!user) {
            addNotification('Please log in to purchase tickets', 'error');
            return;
        }
        
        try {
            addNotification('Processing purchase...', 'info');
            const response = await raffleService.purchaseTickets(selectedEvent.id, ticketQuantity);
            
            if (response.success) {
                setPendingPayment({
                    id: response.payment_id,
                    amount: response.total_amount,
                    ticketCount: response.ticket_count,
                    ticketPrice: response.ticket_price
                });
                setShowPurchaseModal(false);
                setShowPaymentModal(true);
                addNotification('Tickets created! Please upload payment proof.', 'success');
            } else {
                addNotification(response.message || 'Purchase failed', 'error');
            }
        } catch (error) {
            console.error('Purchase error:', error);
            addNotification('Failed to purchase tickets', 'error');
        }
    };

    // Handle file upload
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Check file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            alert('Please upload a valid image (JPEG, PNG) or PDF file');
            return;
        }
        
        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('File size must be less than 5MB');
            return;
        }
        
        setFileName(file.name);
        setUploading(true);
        
        const reader = new FileReader();
        reader.onload = (event) => {
            setProofImage(event.target.result);
            setUploading(false);
        };
        reader.onerror = (error) => {
            console.error('File upload error:', error);
            alert('Failed to upload file. Please try again.');
            setUploading(false);
        };
        reader.readAsDataURL(file);
    };

    // Handle remove file
    const handleRemoveFile = () => {
        setProofImage('');
        setFileName('');
        const fileInput = document.getElementById('payment-proof-input');
        if (fileInput) fileInput.value = '';
    };

    // Handle payment proof upload
    const handleUploadProof = async () => {
        if (!referenceNumber) {
            alert('Please enter the reference number');
            return;
        }
        if (!proofImage) {
            alert('Please upload payment proof');
            return;
        }
        
        if (!pendingPayment) return;
        
        try {
            const response = await raffleService.uploadPaymentProof(
                pendingPayment.id,
                referenceNumber,
                proofImage,
                paymentNotes
            );
            
            if (response.success) {
                setShowPaymentModal(false);
                setPendingPayment(null);
                addNotification(response.message, 'success');
                loadData();
            } else {
                addNotification(response.message || 'Upload failed', 'error');
            }
        } catch (error) {
            console.error('Upload error:', error);
            addNotification('Failed to upload proof', 'error');
        }
    };

    // Handle buy click
    const handleBuyClick = (event) => {
        setSelectedEvent(event);
        setTicketQuantity(1);
        setShowPurchaseModal(true);
    };

    // Handle claim prize
    const handleClaimPrize = async (resultId) => {
        try {
            const response = await raffleService.claimPrize(resultId);
            if (response.success) {
                addNotification(response.message, 'success');
                loadData();
            } else {
                addNotification(response.message || 'Claim failed', 'error');
            }
        } catch (error) {
            console.error('Claim error:', error);
            addNotification('Failed to claim prize', 'error');
        }
    };

    // Countdown Timer Component
    const CountdownTimer = ({ eventId }) => {
        const cd = countdowns[eventId];
        if (!cd) return null;
        
        if (cd.expired) {
            return <span className="countdown-expired">Draw Completed</span>;
        }
        
        if (cd.total <= 0) {
            return <span className="countdown-active">Draw in progress...</span>;
        }
        
        return (
            <div className="countdown-timer">
                <div className="countdown-unit">
                    <span className="countdown-number">{cd.days}</span>
                    <span className="countdown-label">Days</span>
                </div>
                <div className="countdown-unit">
                    <span className="countdown-number">{cd.hours}</span>
                    <span className="countdown-label">Hours</span>
                </div>
                <div className="countdown-unit">
                    <span className="countdown-number">{cd.minutes}</span>
                    <span className="countdown-label">Mins</span>
                </div>
                <div className="countdown-unit">
                    <span className="countdown-number">{cd.seconds}</span>
                    <span className="countdown-label">Secs</span>
                </div>
            </div>
        );
    };

    // Event Card Component
    const EventCard = ({ event, onBuy }) => {
        const available = (event.total_tickets || 0) - (event.tickets_sold || 0);
        const percentage = event.total_tickets > 0 ? ((event.tickets_sold || 0) / event.total_tickets) * 100 : 0;
        
        return (
            <div className="raffle-card">
                <div className="card-header">
                    <h3>{event.title}</h3>
                    <span className={`status-badge ${event.draw_status}`}>
                        {event.draw_status === 'upcoming' ? 'Upcoming' : 
                         event.draw_status === 'active' ? 'Active' : 
                         event.draw_status === 'completed' ? 'Completed' : 'Cancelled'}
                    </span>
                </div>
                
                <p className="card-description">{event.description || 'Join this exciting raffle draw!'}</p>
                
                <div className="ticket-progress">
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${Math.min(percentage, 100)}%` }}></div>
                    </div>
                    <div className="progress-stats">
                        <span>{event.tickets_sold || 0} Sold</span>
                        <span>{available} Available</span>
                    </div>
                </div>
                
                <div className="price-info">
                    <span className="ticket-price">₦{Number(event.ticket_price || 0).toLocaleString()}</span>
                    <span className="per-ticket">per ticket</span>
                </div>
                
                <div className="prize-preview">
                    <i className="fa-solid fa-gift"></i>
                    <span>{event.total_prizes || 0} Prizes to be won!</span>
                </div>
                
                <div className="draw-info">
                    <i className="fa-regular fa-calendar"></i>
                    <span>Draw Date: {event.draw_date ? new Date(event.draw_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }) : 'TBD'}</span>
                </div>
                
                {event.user_tickets > 0 && (
                    <div className="user-tickets">
                        <i className="fa-solid fa-ticket"></i>
                        <span>You have {event.user_tickets} ticket(s)</span>
                    </div>
                )}
                
                <CountdownTimer eventId={event.id} />
                
                {event.draw_status === 'upcoming' && available > 0 && (
                    <button 
                        className="buy-ticket-btn"
                        onClick={() => onBuy(event)}
                    >
                        <i className="fa-solid fa-ticket"></i>
                        Buy Tickets
                    </button>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="raffle-loading">
                <div className="spinner"></div>
                <p>Loading raffle data...</p>
            </div>
        );
    }

    return (
        <div className="raffle-page">
            {/* Header */}
            <div className="raffle-header">
                <h1>
                    <i className="fa-solid fa-ticket"></i>
                    RA Raffle Draw
                </h1>
                <p>Buy tickets, win amazing prizes! Each ticket gives you a chance to win household items.</p>
            </div>
            
            {/* Tabs */}
            <div className="raffle-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`}
                    onClick={() => setActiveTab('events')}
                >
                    <i className="fa-solid fa-calendar"></i>
                    Active Events
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'my-tickets' ? 'active' : ''}`}
                    onClick={() => setActiveTab('my-tickets')}
                >
                    <i className="fa-solid fa-ticket"></i>
                    My Tickets
                    {myTickets.length > 0 && <span className="tab-count">{myTickets.length}</span>}
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'winnings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('winnings')}
                >
                    <i className="fa-solid fa-trophy"></i>
                    My Winnings
                    {myWinnings.length > 0 && <span className="tab-count">{myWinnings.length}</span>}
                </button>
            </div>
            
            {/* Content */}
            <div className="raffle-content">
                {activeTab === 'events' && (
                    <div className="events-grid">
                        {events.length > 0 ? (
                            events.map(event => (
                                <EventCard 
                                    key={event.id} 
                                    event={event} 
                                    onBuy={handleBuyClick}
                                />
                            ))
                        ) : (
                            <div className="empty-state">
                                <i className="fa-solid fa-calendar-xmark"></i>
                                <h3>No Active Events</h3>
                                <p>There are no active raffle events at the moment. Check back soon!</p>
                            </div>
                        )}
                    </div>
                )}
                
                {activeTab === 'my-tickets' && (
                    <div className="tickets-grid">
                        {myTickets.length > 0 ? (
                            myTickets.map(ticket => (
                                <div key={ticket.id} className="ticket-card">
                                    <div className="ticket-number">
                                        <i className="fa-solid fa-ticket"></i>
                                        <span>{ticket.ticket_number}</span>
                                    </div>
                                    <div className="ticket-event">{ticket.event_title}</div>
                                    <div className="ticket-status">
                                        <span className={`status-badge ${ticket.payment_status}`}>
                                            {ticket.payment_status}
                                        </span>
                                        {ticket.is_winner && (
                                            <span className="winner-badge">
                                                <i className="fa-solid fa-trophy"></i> Winner!
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <i className="fa-solid fa-ticket"></i>
                                <h3>No Tickets Yet</h3>
                                <p>You haven't purchased any raffle tickets yet. Join an event to get started!</p>
                            </div>
                        )}
                    </div>
                )}
                
                {activeTab === 'winnings' && (
                    <div className="winnings-grid">
                        {myWinnings.length > 0 ? (
                            myWinnings.map(winning => (
                                <div key={winning.id} className="winning-card">
                                    <div className="winning-icon">
                                        <i className="fa-solid fa-trophy"></i>
                                    </div>
                                    <div className="winning-details">
                                        <h4>{winning.prize_name}</h4>
                                        <p className="winning-event">{winning.event_title}</p>
                                        <p className="winning-ticket">Ticket: {winning.ticket_number}</p>
                                        {winning.prize_value > 0 && (
                                            <p className="winning-value">Value: ₦{Number(winning.prize_value).toLocaleString()}</p>
                                        )}
                                    </div>
                                    <div className="winning-actions">
                                        {!winning.claimed && (
                                            <button 
                                                className="claim-btn"
                                                onClick={() => handleClaimPrize(winning.id)}
                                            >
                                                Claim Prize
                                            </button>
                                        )}
                                        {winning.claimed && (
                                            <span className="claimed-badge">
                                                <i className="fa-solid fa-check-circle"></i> Claimed
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <i className="fa-solid fa-trophy"></i>
                                <h3>No Winnings Yet</h3>
                                <p>You haven't won any prizes yet. Buy tickets to increase your chances!</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            {/* Purchase Modal */}
            {showPurchaseModal && selectedEvent && (
                <div className="modal-overlay" onClick={() => setShowPurchaseModal(false)}>
                    <div className="purchase-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Buy Raffle Tickets</h3>
                            <button className="close-btn" onClick={() => setShowPurchaseModal(false)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="event-summary">
                                <h4>{selectedEvent.title}</h4>
                                <p className="price">₦{Number(selectedEvent.ticket_price).toLocaleString()} per ticket</p>
                                <p className="available">Available tickets: {(selectedEvent.total_tickets || 0) - (selectedEvent.tickets_sold || 0)}</p>
                            </div>
                            
                            <div className="quantity-selector">
                                <label>Number of Tickets</label>
                                <div className="quantity-controls">
                                    <button 
                                        onClick={() => setTicketQuantity(Math.max(1, ticketQuantity - 1))}
                                        disabled={ticketQuantity <= 1}
                                    >
                                        <i className="fa-solid fa-minus"></i>
                                    </button>
                                    <input 
                                        type="number" 
                                        value={ticketQuantity}
                                        onChange={(e) => setTicketQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                        min="1"
                                        max={selectedEvent.max_tickets_per_member || 50}
                                    />
                                    <button 
                                        onClick={() => setTicketQuantity(Math.min(selectedEvent.max_tickets_per_member || 50, ticketQuantity + 1))}
                                        disabled={ticketQuantity >= (selectedEvent.max_tickets_per_member || 50)}
                                    >
                                        <i className="fa-solid fa-plus"></i>
                                    </button>
                                </div>
                                <p className="max-note">Maximum {selectedEvent.max_tickets_per_member || 50} tickets per member</p>
                            </div>
                            
                            <div className="total-amount">
                                <span>Total Amount:</span>
                                <strong>₦{Number((selectedEvent.ticket_price || 0) * ticketQuantity).toLocaleString()}</strong>
                            </div>
                            
                            <div className="info-box">
                                <i className="fa-solid fa-info-circle"></i>
                                <p>After purchase, you'll need to upload payment proof for verification.</p>
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowPurchaseModal(false)}>
                                Cancel
                            </button>
                            <button className="btn-purchase" onClick={handlePurchase}>
                                <i className="fa-solid fa-ticket"></i>
                                Purchase Tickets
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Payment Modal - COMPLETE WORKING VERSION */}
            {showPaymentModal && pendingPayment && (
                <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
                    <div className="payment-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Payment Details</h3>
                            <button className="close-btn" onClick={() => setShowPaymentModal(false)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="payment-summary">
                                <div className="summary-row">
                                    <span>Tickets:</span>
                                    <span>{pendingPayment.ticketCount} x ₦{Number(pendingPayment.ticketPrice).toLocaleString()}</span>
                                </div>
                                <div className="summary-row total">
                                    <span>Total:</span>
                                    <strong>₦{Number(pendingPayment.amount).toLocaleString()}</strong>
                                </div>
                            </div>
                            
                            <div className="bank-details">
                                <h4>Bank Transfer Details</h4>
                                <div className="bank-info">
                                    <p><strong>Bank:</strong> {bankDetails.bank_name || 'Access Bank'}</p>
                                    <p><strong>Account Name:</strong> {bankDetails.bank_account_name || 'NLNGRA Club'}</p>
                                    <p><strong>Account Number:</strong> {bankDetails.bank_account_number || '1234567890'}</p>
                                </div>
                            </div>
                            
                            <div className="upload-section">
                                <h4>Upload Payment Proof</h4>
                                
                                <div className="form-group">
                                    <label>Reference Number *</label>
                                    <input 
                                        type="text"
                                        placeholder="Enter transaction reference"
                                        value={referenceNumber}
                                        onChange={(e) => setReferenceNumber(e.target.value)}
                                        required
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Upload Receipt/Screenshot *</label>
                                    <div className="file-upload-container">
                                        <input 
                                            type="file"
                                            id="payment-proof-input"
                                            accept="image/jpeg,image/png,image/jpg,application/pdf"
                                            onChange={handleFileUpload}
                                            style={{ display: 'none' }}
                                        />
                                        <button 
                                            type="button"
                                            className="file-upload-btn"
                                            onClick={() => document.getElementById('payment-proof-input').click()}
                                            disabled={uploading}
                                        >
                                            <i className="fa-solid fa-cloud-upload-alt"></i>
                                            {uploading ? 'Uploading...' : (proofImage ? 'Change File' : 'Choose File')}
                                        </button>
                                        
                                        {fileName && (
                                            <div className="selected-file">
                                                <i className="fa-solid fa-file"></i>
                                                <span>{fileName}</span>
                                                <button 
                                                    type="button"
                                                    className="remove-file"
                                                    onClick={handleRemoveFile}
                                                >
                                                    <i className="fa-solid fa-times"></i>
                                                </button>
                                            </div>
                                        )}
                                        
                                        {proofImage && proofImage.startsWith('data:image') && (
                                            <div className="file-preview">
                                                <img src={proofImage} alt="Payment proof preview" className="preview-image" />
                                                <button 
                                                    type="button"
                                                    className="preview-close"
                                                    onClick={handleRemoveFile}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        )}
                                        
                                        {proofImage && proofImage.startsWith('data:application/pdf') && (
                                            <div className="pdf-preview">
                                                <i className="fa-solid fa-file-pdf"></i>
                                                <span>PDF Document Uploaded</span>
                                                <button 
                                                    type="button"
                                                    className="remove-file"
                                                    onClick={handleRemoveFile}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <small className="file-hint">
                                        Supported formats: JPG, PNG, PDF (Max 5MB)
                                    </small>
                                </div>
                                
                                <div className="form-group">
                                    <label>Notes (Optional)</label>
                                    <textarea 
                                        rows="3"
                                        placeholder="Any additional information..."
                                        value={paymentNotes}
                                        onChange={(e) => setPaymentNotes(e.target.value)}
                                    />
                                </div>
                                
                                <div className="info-box warning">
                                    <i className="fa-solid fa-exclamation-triangle"></i>
                                    <p>Please ensure the payment proof is clear and shows the reference number.</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowPaymentModal(false)}>
                                Cancel
                            </button>
                            <button 
                                className="btn-submit"
                                onClick={handleUploadProof}
                                disabled={!referenceNumber || !proofImage || uploading}
                            >
                                <i className="fa-solid fa-cloud-upload-alt"></i>
                                Submit Payment Proof
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RafflePage;